// Contract 29 §9 — the onboarding funnel. Ports `OnboardingModal.vue:90-121` onto the shared GTM
// adapter; the `completion_time` per step is what made the legacy watcher worth keeping.

import { ONBOARDING_STEPS, type OnboardingStepKey } from '../models/onboarding.types'
import { stepNumber } from '../service/onboardingSteps'

import { useAccount } from '@/features/account'
import { pushToDataLayer } from '@/shared/lib/analytics/gtm'

/** Entered-at, per step key. Module-scoped so a step remount does not restart its clock. */
const enteredAt = new Map<OnboardingStepKey, number>()

export interface UseOnboardingAnalytics {
  trackStepEnter: (step: OnboardingStepKey) => void
  trackStepLeave: (step: OnboardingStepKey) => void
  trackCompleted: (step: OnboardingStepKey) => void
  reset: () => void
}

export function useOnboardingAnalytics(now: () => number = Date.now): UseOnboardingAnalytics {
  const account = useAccount()
  const userId = (): unknown => account.userId.value ?? null

  function secondsOn(step: OnboardingStepKey): number | null {
    const started = enteredAt.get(step)
    return started === undefined ? null : Math.round((now() - started) / 1000)
  }

  function trackStepEnter(step: OnboardingStepKey): void {
    enteredAt.set(step, now())
    pushToDataLayer({
      event: 'onboarding_started',
      user_id: userId(),
      step_name: step,
      step_index: stepNumber(step),
      event_type: 'Funnel',
    })
  }

  function trackStepLeave(step: OnboardingStepKey): void {
    pushToDataLayer({
      event: 'onboarding_completed',
      user_id: userId(),
      step_name: step,
      step_index: stepNumber(step),
      completion_time: secondsOn(step),
      event_type: 'Funnel',
    })
    enteredAt.delete(step)
  }

  return {
    trackStepEnter,
    trackStepLeave,
    trackCompleted: (step) => {
      trackStepLeave(step)
      // The journey-level close the legacy phantom "step 5" never managed to fire (§3.5).
      pushToDataLayer({
        event: 'onboarding_completed',
        user_id: userId(),
        step_name: 'journey',
        step_index: ONBOARDING_STEPS.length,
        event_type: 'Funnel',
      })
    },
    reset: () => enteredAt.clear(),
  }
}
